import { useEffect, useState } from "react";
import { Sparkles, ChevronLeft, ChevronRight, Loader2, BookOpen } from "lucide-react";
import { motion } from "framer-motion";
import PageShell from "../components/PageShell";
import PremiumGate from "../components/PremiumGate";
import { usePremium } from "../context/PremiumContext";
import { getDevotional, getDevotionalTopics, API } from "../api";
import { getDevoTopics, setDevoTopics } from "../lib/personal";

export default function Devotionals() {
  const { isPremium } = usePremium();
  const [allTopics, setAllTopics] = useState([]);
  const [active, setActive] = useState(getDevoTopics()[0] || "faith");
  const [day, setDay] = useState(0); // 0 => let backend pick today's day
  const [devo, setDevo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDevotionalTopics().then(setAllTopics).catch(() => {});
  }, []);

  useEffect(() => {
    let on = true;
    setLoading(true);
    setDevo(null);
    getDevotional(active, day)
      .then((d) => { if (on) setDevo(d); })
      .catch(() => { if (on) setDevo({ error: true }); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [active, day]);

  const pickTopic = (t) => {
    setActive(t);
    setDay(0);
    const prefs = getDevoTopics();
    if (!prefs.includes(t)) setDevoTopics([t, ...prefs].slice(0, 12));
  };

  const seriesLen = devo?.series_len || 7;
  const curDay = devo?.day || 1;
  const goDay = (n) => { if (n >= 1 && n <= seriesLen) setDay(n); };

  return (
    <PageShell
      title="Daily Devotionals"
      subtitle="Genuine Spurgeon readings, grouped by theme"
      icon={Sparkles}
      accent={{ a: "#E0B85C", b: "#5C8A6E", c: "#2E5A66" }}
      testid="devotionals-page"
    >
      {!isPremium ? (
        <PremiumGate feature="Daily Devotionals" perks={["Spurgeon's Morning & Evening", "7-day series per theme", "A fresh story & image every day"]} />
      ) : (
        <>
          {/* topic selector (single active series) */}
          <div className="mb-8">
            <p className="font-ui mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary/80">Choose a theme</p>
            <div className="flex flex-wrap gap-2">
              {(allTopics.length ? allTopics : ["faith"]).map((t) => (
                <button
                  key={t}
                  data-testid={`topic-${t}`}
                  onClick={() => pickTopic(t)}
                  className={`font-ui rounded-full border px-4 py-1.5 text-sm capitalize transition-all ${
                    active === t
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background/40 hover:bg-muted"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* day navigation within the series */}
          <div className="mb-6 flex items-center justify-between gap-3" data-testid="series-nav">
            <button
              onClick={() => goDay(curDay - 1)}
              disabled={curDay <= 1 || loading}
              data-testid="devo-prev-day"
              className="font-ui flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm transition-colors hover:bg-muted disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </button>
            <div className="flex items-center gap-1.5">
              {Array.from({ length: seriesLen }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => goDay(i + 1)}
                  data-testid={`devo-day-dot-${i + 1}`}
                  aria-label={`Day ${i + 1}`}
                  className={`h-2.5 rounded-full transition-all ${
                    curDay === i + 1 ? "w-6 bg-primary" : "w-2.5 bg-muted-foreground/40 hover:bg-muted-foreground"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={() => goDay(curDay + 1)}
              disabled={curDay >= seriesLen || loading}
              data-testid="devo-next-day"
              className="font-ui flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm transition-colors hover:bg-muted disabled:opacity-40"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center gap-3 rounded-3xl border border-border bg-background/40 p-10 backdrop-blur-md">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="font-ui text-sm text-muted-foreground">Preparing your devotional…</span>
            </div>
          ) : devo?.error ? (
            <p className="font-ui text-sm text-muted-foreground">Couldn’t load a devotional right now. Try again in a moment.</p>
          ) : devo ? (
            <motion.article
              key={`${devo.topic}-${devo.day}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="overflow-hidden rounded-3xl border border-border bg-background/50 backdrop-blur-md"
              data-testid="devotional-card"
            >
              {devo.has_image && (
                <div className="relative h-52 w-full overflow-hidden md:h-64">
                  <img
                    src={`${API}/devotional/image/${devo.topic}/${devo.day}`}
                    alt={devo.title}
                    data-testid="devotional-image"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                  <div className="absolute bottom-4 left-6 right-6">
                    <p className="font-ui text-xs font-semibold uppercase tracking-[0.3em] text-primary capitalize">
                      {devo.topic} · Day {devo.day} of {devo.series_len}
                    </p>
                    <h2 className="font-serif-display text-3xl tracking-tight md:text-4xl">{devo.title}</h2>
                  </div>
                </div>
              )}

              <div className="p-8 md:p-12">
                {!devo.has_image && (
                  <>
                    <p className="font-ui mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-primary capitalize">
                      {devo.topic} · Day {devo.day} of {devo.series_len}
                    </p>
                    <h2 className="font-serif-display text-3xl tracking-tight md:text-4xl">{devo.title}</h2>
                  </>
                )}

                <blockquote className="reading-text my-7 border-l-2 border-primary pl-5">
                  <p className="font-serif-display text-xl italic leading-relaxed md:text-2xl">“{devo.verse_text}”</p>
                  <cite className="font-ui mt-2 block text-sm not-italic text-primary">{devo.verse_ref}</cite>
                </blockquote>

                <div className="reading-text space-y-4 text-base leading-relaxed text-foreground/90 md:text-lg">
                  {(devo.reflection || "").split("\n\n").map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                <p className="font-ui mt-3 text-xs italic text-muted-foreground">
                  — {devo.author}, {devo.source}
                </p>

                {devo.story && (
                  <div className="mt-8 rounded-2xl border border-primary/20 bg-primary/5 p-6" data-testid="devotional-story">
                    <p className="font-ui mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                      <BookOpen className="h-3.5 w-3.5" /> A story for today
                    </p>
                    <p className="reading-text leading-relaxed text-foreground/90">{devo.story}</p>
                  </div>
                )}

                {devo.prayer && (
                  <div className="mt-6 rounded-2xl bg-muted/50 p-6">
                    <p className="font-ui mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary">Prayer</p>
                    <p className="reading-text italic leading-relaxed text-foreground/90">{devo.prayer}</p>
                  </div>
                )}
              </div>
            </motion.article>
          ) : null}
        </>
      )}
    </PageShell>
  );
}
